import { useEffect, useRef } from "react";
import type { ContractId, MissionObjective, MissionView } from "@frontier/content/contracts";
import type { PublicEventView } from "@frontier/content/events";
import { MissionReadout } from "./MissionReadout";
import type { TutorialGuidance } from "./onboarding-ui";
import { pressHandlers } from "./press-handlers";
import "./mission-journal.css";
import { credits } from "./ui-labels";
import { RelayReadout } from "./RelayReadout";
import { currentContractDescription } from "./contract-description";

interface JournalProps {
  mission: MissionView | null;
  missions: readonly MissionView[];
  onFocusContract: (id: ContractId) => void;
  guidance: TutorialGuidance | null;
  event: PublicEventView | null;
  eventObjective: MissionObjective | null;
  eventPresence?: number | undefined;
}

export function MissionJournalPanel({
  mission,
  missions,
  onFocusContract,
  guidance,
  event,
  eventObjective,
  eventPresence,
  onClose,
}: JournalProps & { onClose: () => void }) {
  return (
    <section
      className="mission-journal-panel"
      role="dialog"
      aria-label="Журнал заданий"
      aria-modal="false"
    >
      <div className="mission-journal-heading">
        <b>ЖУРНАЛ ЗАДАНИЙ</b>
        <button aria-label="Закрыть журнал заданий" {...pressHandlers(onClose)}>
          ×
        </button>
      </div>
      {(mission || (event && eventObjective)) && (
        <p className="journal-tracked" role="status">
          НА КАРТЕ · <b>{event && eventObjective ? event.title : mission?.title}</b>
        </p>
      )}
      {event && eventObjective && (
        <section className="journal-event" aria-label="Отслеживаемое событие">
          <span className="eyebrow">СОБЫТИЕ / {event.title}</span>
          <p>{eventObjective.name}</p>
          <small>
            {Math.round(event.progress)}/{event.goal}
          </small>
          <p>{event.terms.description}</p>
          <small className="event-condition">{event.terms.condition}</small>
          <RelayReadout event={event} presence={eventPresence} />
          <small>
            {event.type === "relay" ? "За победу и присутствие" : "За участие и успех"}: {credits(event.terms.credits)} КР · {event.terms.components} комп.
          </small>
          {event.workBlocked && (
            <strong className="event-work-blocked">ОХРАНА МЕШАЕТ РАЗБОРУ</strong>
          )}
        </section>
      )}
      <MissionReadout
        mission={mission}
        missions={missions}
        onFocusContract={onFocusContract}
        contractTracked={!eventObjective}
        guidance={guidance}
      />
      {mission ? (
        <p className="journal-description">{currentContractDescription(mission.terms.description)}</p>
      ) : (
        <p className="journal-empty">Нет активных контрактов. Заказы доступны на станции.</p>
      )}
    </section>
  );
}

export function MissionJournal(props: JournalProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { open, onOpenChange } = props;
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !root.current?.contains(event.target)) onOpenChange(false);
    };
    window.addEventListener("keydown", close);
    document.addEventListener("pointerdown", outside);
    return () => {
      window.removeEventListener("keydown", close);
      document.removeEventListener("pointerdown", outside);
    };
  }, [open, onOpenChange]);
  return (
    <div className="mission-journal" ref={root}>
      <button
        className="journal-button"
        aria-label="Журнал заданий"
        aria-expanded={open}
        title={`Задания${props.event && props.eventObjective ? ` · ${props.event.title}` : props.mission ? ` · ${props.mission.title}` : ""}`}
        {...pressHandlers(() => onOpenChange(!open))}
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 4h14v16H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm0 0v16M9 8h7m-7 4h7m-7 4h4" />
        </svg>
      </button>
      {open && <MissionJournalPanel {...props} onClose={() => onOpenChange(false)} />}
    </div>
  );
}
